import { FC, Fragment, useState } from 'react'
import { BookingContext } from '../../parts/BookingContext'
import { useApp } from '../../context/useApp'
import { Switch } from '../../parts/inputs/Switch'
import { _, load, parseNum, deleteItem, parseProductPrice } from '../../utils/helpers'
import { HelpButton } from '../../parts/HelpButton'
import { Icon } from '../../parts/Icon'
import { DEFAULT_DIALOG_HANDLE, metrics, ROUTES, SCREEN_BREAKPOINT_1, PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX, rumba, SURCHARGE_NAME } from '../../utils/symbols'
import { useDefaultDialogs } from '../../hooks/useDefaultDialogs'
import {captureException, captureMessage} from "@sentry/react"
import { Stripe, StripeElements } from '@stripe/stripe-js'
import { Product } from '../../types'

interface AsideProps {
	clientSecret?: string
	stripeComplete: boolean,
	stripe?: Stripe | null | undefined
	stripeState?: StripeElements | null 
}

export const Aside: FC<AsideProps> = ({ clientSecret, stripeComplete, stripe, stripeState }) => {
	const {
		state: { booking, shifts, dialogHandles, screen, products },
		actions: { navigate, setMapVisible },
	} = useApp()

	const [loading, setLoading] = useState(false)

	const dialogHandle = dialogHandles.get('app-dialog') ?? DEFAULT_DIALOG_HANDLE
	const summary = booking.getBookingSummary()
	const shift = shifts.find((shift) => shift.id === booking.shiftId)
	const errors = booking.validatePersonalDetail({ shift: shift! })
	const isAbleToPay = errors.length === 0 && stripeComplete

	function checkRequiredProducts() {
        if (booking.hasTableSelection() && booking.step !== "products") {
            return true
        }
        const requiredProduct = products.find(p => p.required && booking.getProductQty(p.id) < booking.pax)
        if (!requiredProduct) return true
        dialogHandle.openDialog({
            dialog: "required-product", 
            product: requiredProduct,
            toAdd: booking.pax - booking.getProductQty(requiredProduct.id),
        })
        return false
    }

	const handleFormErrors = () => {
		if (!booking.hasTableSelection() && booking.step !== "personal-details") {
			// check for required products
			const requiredProductsOk = checkRequiredProducts()
			if (!requiredProductsOk) {
				return
			}
			booking.setBookingStep("personal-details").render()
			navigate(ROUTES.PERSONAL_DETAILS)
		}

		
		errors.forEach((error, index) => {
			const inputContainer = document.getElementById(`${error.associatedTo}-container`) as HTMLDivElement
			if (!inputContainer) return 

			if (index === 0) {
				const input = inputContainer.querySelector('input')
				if (input) input.focus()
				const container = document.querySelector('main')
				if (container) container.scrollTo({top: inputContainer.offsetTop - 50, behavior: 'smooth'})
			}

			inputContainer.classList.add('error-visible')
		})
	}

	const cancellationFee = products.find(product => product.category === "insurance" && product.name !== SURCHARGE_NAME)
	const handleProductQtyChange = (product: Product, newQty: number) => {
			const isFixedToPax = PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX.includes(product.id)
			const qty = isFixedToPax ? newQty * booking.pax : newQty
			console.log(newQty)
			booking.handleProductWithOutTables(product, qty).render()
		}

	const errorsDescription =
		'To continue with the payment, fix the following errors: ' +
		errors
			.map((error) => {
				return `${error.associatedTo} ${_(error.message)}`
			})
			.join(',')

	return (
		<div className="fs-0  aside" role="region">
			{!booking.hasTableSelection() && screen.width >= SCREEN_BREAKPOINT_1 && ["tickets","products"].includes(booking.step) && (
				<button 
					className="test-ab-map-button"
					onClick={() => setMapVisible(true)}
				>
					<span>{_("0ab_seesi__")}</span>
					<Icon icon="map"/>
				</button>
			)}
         {/* Alternative: oculta el boton de edicion */}
			<div className={`r4 booking-summary mb-8 ${!booking.hasTableSelection() ? "alternative" : ""}`}>
				<section role="group" aria-label={_('0ar_booki__')} className="py-9 px-10">
					<div className={`w-100 df jcsb aic mb-7 booking-summary-header d-ib`}>
						<h3 id="section-1-title" className="text s20-600 white title" aria-hidden>
							{_('5su_purch__')}
						</h3>
						<button
							onClick={() => {
								booking.setBookingStep('products').render()
								navigate(ROUTES.TABLE_LAYOUT)
							}}
							aria-label={_('0ar_backt__')}
							className="button p-0 text underline s18-600 black right b-0"
						>
							<Icon icon="edit"/>
						</button>
					</div>
					<BookingContext date shift zone white={booking.hasTableSelection()} />
				</section>
				<section className="p-10" role="group" aria-label={_('5su_purch__')}>
						{Object.values(summary.products).map((productTypeSummary, index) => (
							<Fragment key={index}>
								{' '}
								{productTypeSummary.items.map((product, index) => (
									<p
										key={index}
										className="df aic jcsb w-100 text s18-400 mb-5 booking-line"
										aria-label={`${product.qty} ${product.name}, ${parseNum(
											'EUR',
											product.qty * product.price
										)}`}
									>
										<span aria-hidden className="text left">{`${product.qty}x ${product.name}`}</span>
										<span className="text s20-600 price ml-5" aria-hidden>
											{   product.discounts.length > 0  && 
												<span className="text softer strikethrough mr-4 s18-500">
													{parseNum('EUR', product.originalPrice)}       
												</span>
											}
											{parseNum('EUR', product.price)}
										</span>
									</p>
								))}{' '}
							</Fragment>
						))}
						{booking.getTotalAmount() > 0 && (
							<hr className="w100 my-7" aria-hidden />
						)}
					<p
						className="df aic jcsb w-100 text s18-600 booking-summary-total"
						aria-label={`${_('0ge_total__')} ${parseNum('EUR', summary.total)}`}
					>
						<span aria-hidden className="text left">
							{_('0ge_total__')}
						</span>
						<span aria-hidden className="text left right">
							{parseNum('EUR', summary.total)}
						</span>
					</p>
					{!clientSecret && (
						<button
							disabled={loading || booking.getAdults() === 0}
							aria-label={isAbleToPay ? _('0ar_click_t') : errorsDescription}
							className="button r3 w-100 py-7 px-10 mt-9 text s20-500 action-2 b-0 button-hover capitalized"
							type='button'
							onClick={handleFormErrors}
						>
							{_("0ge_conti__")}
						</button>
					)}
				</section>
			</div>
			{screen.width < SCREEN_BREAKPOINT_1 && (
			
				<p id="conditions"  
					className="text s14-500 softer my-8" 
					aria-label={_('5tc_bysub__','reservas@cardamomo.com')}
					dangerouslySetInnerHTML={{__html: `<span aria-hidden="true">*${_('5tc_bysub__')}</span><a href="mailto:reservas@cardamomo.com">reservas@cardamomo.com</a>`}}
				>
				</p>
			)}
			{
				booking.step === "personal-details" && (
					<HelpButton className="w-100 mb-7" />
				)
			}
			{	cancellationFee && booking.step !== "personal-details" ? (
                <div className={`white-box mt-7 ${booking.pax === 0 ? "disabled" : ""}`}>
                    <button 
                        className="df aic"
                    >
                        <span className="text s18-500">{_("0ab_cance__")}</span>
                        <Icon 
							icon="info" 
							onClick={() =>  
								dialogHandle.openDialog({
									className: 'product w-100 h-100 df aic jcc',
									dialog: 'product', 
									product: cancellationFee
								})
							}
						/>
                    </button>
                    <div className="df aic jcfe">
                        <p className="text s18-600 right mr-5">
                            {parseProductPrice(cancellationFee)}
                        </p>
                        <Switch
                            disabled={booking.pax === 0}
                            value={booking.getProductQty(cancellationFee.id)}
                            onChange={(checked) => handleProductQtyChange(cancellationFee, checked ? 1 : 0)}
                        />
                    </div>
                </div>
			) : null}
		</div>
	)
}
